import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { formatDateTime } from '../../utils/formatDateTime';

export const EditableDataGrid = ({ 
  columns, 
  data, 
  onCellChange, 
  onSave,
  selectedRow,
  onRowClick,
  expandedRows = new Set(),
  onRowExpand,
  historyData = {},
  onHistoryRequest,
  loading = false,
  observerTarget = null,
  getRowClassName = null,
  renderExpandedToolbar = null,
}) => {
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [pendingChanges, setPendingChanges] = useState({});
  const [tooltip, setTooltip] = useState({ show: false, text: '', x: 0, y: 0 });
  const inputRef = useRef(null);
  const historyDataRef = useRef(historyData);
  
  // historyDataの最新値をrefで保持
  useEffect(() => {
    historyDataRef.current = historyData;
  }, [historyData]);
  
  // selectedRowのproduct_codeを取得（比較用）
  const selectedProductCode = useMemo(() => {
    return selectedRow?.product_code || null;
  }, [selectedRow?.product_code]);

  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingCell]);

  const handleCellClick = useCallback((row, col, e) => {
    e.stopPropagation();
    // readonlyカラムは履歴展開をトリガー
    if (col.readonly) {
      if (onRowExpand) {
        onRowExpand(row.product_code);
      }
      // 履歴データがまだ取得されていない場合は取得（refから最新値を取得）
      if (onHistoryRequest && !historyDataRef.current[row.product_code]) {
        onHistoryRequest(row.product_code);
      }
      return;
    }
    // より安全な区切り文字を使用（product_codeにアンダースコアが含まれる可能性があるため）
    const cellKey = `${row.product_code}::${col.field}`;
    setEditingCell(cellKey);
    
    // 保存待ちの変更がある場合はその値を使用、なければ元の値を使用
    const hasPendingChange = pendingChanges[cellKey];
    let displayValue;
    if (hasPendingChange) {
      displayValue = hasPendingChange.newValue;
    } else {
      displayValue = row[col.field];
    }
    
    // 数値型の場合は文字列に変換、null/undefinedの場合は空文字列
    setEditValue(displayValue != null ? String(displayValue) : '');
  }, [onRowExpand, onHistoryRequest, pendingChanges]);

  const handleCellBlur = useCallback(() => {
    if (editingCell) {
      const [productCode, field] = editingCell.split('::');
      const row = data.find(r => r.product_code === productCode);
      if (!row) {
        setEditingCell(null);
        return;
      }
      
      const col = columns.find(c => c.field === field);
      const currentValue = row[field];
      const currentValueStr = currentValue != null ? String(currentValue) : '';
      const editValueTrimmed = editValue.trim();
      
      // 値が変更されているかチェック（型を考慮）
      let hasChanged = false;
      if (col && col.type === 'number') {
        // 数値型の場合
        const currentNum = currentValue != null ? Number(currentValue) : null;
        const editNum = editValueTrimmed !== '' ? Number(editValueTrimmed) : null;
        hasChanged = currentNum !== editNum;
      } else {
        // 文字列型の場合
        hasChanged = currentValueStr !== editValueTrimmed;
      }
      
      if (hasChanged) {
        setPendingChanges(prev => ({
          ...prev,
          [editingCell]: {
            productCode,
            field,
            oldValue: currentValue,
            newValue: editValueTrimmed,
            colType: col?.type
          }
        }));
        if (onCellChange) {
          onCellChange(productCode, field, editValueTrimmed, currentValue);
        }
      }
      setEditingCell(null);
    }
  }, [editingCell, editValue, data, onCellChange, columns]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!editingCell) return;
      
      const [productCode, field] = editingCell.split('::');
      const currentRowIndex = data.findIndex(r => r.product_code === productCode);
      
      if (currentRowIndex === -1) {
        handleCellBlur();
        return;
      }
      
      // 現在の行の現在の列を取得
      const currentColIndex = columns.findIndex(c => c.field === field);
      if (currentColIndex === -1) {
        handleCellBlur();
        return;
      }
      
      // まず現在のセルを保存
      handleCellBlur();
      
      // 次の行が存在するかチェック
      if (currentRowIndex < data.length - 1) {
        const nextRow = data[currentRowIndex + 1];
        // 同じ列が編集可能かチェック
        const nextCol = columns[currentColIndex];
        if (nextCol && !nextCol.readonly) {
          // 少し遅延を入れて、前のセルの保存処理が完了してから次のセルを開く
          setTimeout(() => {
            const nextCellKey = `${nextRow.product_code}::${nextCol.field}`;
            setEditingCell(nextCellKey);
            
            // 保存待ちの変更がある場合はその値を使用、なければ元の値を使用
            const hasPendingChange = pendingChanges[nextCellKey];
            let displayValue;
            if (hasPendingChange) {
              displayValue = hasPendingChange.newValue;
            } else {
              displayValue = nextRow[nextCol.field];
            }
            
            setEditValue(displayValue != null ? String(displayValue) : '');
          }, 10);
        }
      }
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setEditValue('');
    } else if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      // 矢印キーでの移動
      e.preventDefault();
      if (!editingCell) return;
      
      const [productCode, field] = editingCell.split('::');
      const currentRowIndex = data.findIndex(r => r.product_code === productCode);
      const currentColIndex = columns.findIndex(c => c.field === field);
      
      if (currentRowIndex === -1 || currentColIndex === -1) {
        return;
      }
      
      // まず現在のセルを保存
      handleCellBlur();
      
      let nextRowIndex = currentRowIndex;
      let nextColIndex = currentColIndex;
      
      // 矢印キーの方向に応じて次のセルの位置を計算
      if (e.key === 'ArrowUp') {
        nextRowIndex = Math.max(0, currentRowIndex - 1);
      } else if (e.key === 'ArrowDown') {
        nextRowIndex = Math.min(data.length - 1, currentRowIndex + 1);
      } else if (e.key === 'ArrowLeft') {
        // 左に移動：編集可能な列を探す（現在の列より前）
        for (let i = currentColIndex - 1; i >= 0; i--) {
          if (!columns[i].readonly) {
            nextColIndex = i;
            break;
          }
        }
      } else if (e.key === 'ArrowRight') {
        // 右に移動：編集可能な列を探す（現在の列より後）
        for (let i = currentColIndex + 1; i < columns.length; i++) {
          if (!columns[i].readonly) {
            nextColIndex = i;
            break;
          }
        }
      }
      
      // 次のセルが編集可能かチェック
      const nextRow = data[nextRowIndex];
      const nextCol = columns[nextColIndex];
      
      if (nextRow && nextCol && !nextCol.readonly) {
        // 少し遅延を入れて、前のセルの保存処理が完了してから次のセルを開く
        setTimeout(() => {
          const nextCellKey = `${nextRow.product_code}::${nextCol.field}`;
          setEditingCell(nextCellKey);
          
          // 保存待ちの変更がある場合はその値を使用、なければ元の値を使用
          const hasPendingChange = pendingChanges[nextCellKey];
          let displayValue;
          if (hasPendingChange) {
            displayValue = hasPendingChange.newValue;
          } else {
            displayValue = nextRow[nextCol.field];
          }
          
          setEditValue(displayValue != null ? String(displayValue) : '');
        }, 10);
      }
    }
  }, [handleCellBlur, editingCell, data, columns, pendingChanges]);

  const handleSaveAll = useCallback(async () => {
    if (Object.keys(pendingChanges).length === 0) {
      alert('保存する変更がありません');
      return;
    }

    const reason = window.prompt('変更理由を入力してください（必須）:');
    if (!reason || reason.trim() === '') {
      alert('変更理由は必須です');
      return;
    }

    try {
      const updates = Object.values(pendingChanges).reduce((acc, change) => {
        if (!acc[change.productCode]) {
          acc[change.productCode] = { reason };
        }
        // 数値型のフィールドは数値に変換
        if (change.colType === 'number') {
          const numValue = change.newValue !== '' ? Number(change.newValue) : null;
          acc[change.productCode][change.field] = isNaN(numValue) ? null : numValue;
        } else {
          // 文字列型のフィールド: 空文字列も許可（空文字列をnullに変換しない）
          // undefinedやnullの場合のみnullにする
          acc[change.productCode][change.field] = change.newValue !== undefined && change.newValue !== null ? change.newValue : null;
        }
        return acc;
      }, {});

      for (const [productCode, update] of Object.entries(updates)) {
        await onSave(productCode, update);
      }

      setPendingChanges({});
      alert('保存が完了しました');
    } catch (error) {
      console.error('保存エラー:', error);
      alert('保存に失敗しました: ' + (error.message || '不明なエラー'));
    }
  }, [pendingChanges, onSave]);

  const renderCellContent = useCallback((row, col, rowIdx) => {
    const cellKey = `${row.product_code}::${col.field}`;
    const isEditing = editingCell === cellKey;
    const hasPendingChange = pendingChanges[cellKey];

    if (isEditing) {
      return (
        <input
          ref={inputRef}
          type={col.type || 'text'}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleCellBlur}
          onKeyDown={handleKeyDown}
          className="w-full px-2 py-1 text-sm border-2 border-blue-500 rounded focus:outline-none"
          onClick={(e) => e.stopPropagation()}
        />
      );
    }

    // 保存待ちの変更がある場合は新しい値を表示
    let displayValue;
    if (hasPendingChange) {
      displayValue = hasPendingChange.newValue !== '' ? hasPendingChange.newValue : '-';
    } else {
      displayValue = col.render ? col.render(row, rowIdx) : (row[col.field] || '-');
    }
    
    return displayValue;
  }, [editingCell, editValue, pendingChanges, handleCellBlur, handleKeyDown, columns]);

  const handleCellMouseEnter = useCallback((e, row, col) => {
    const cellKey = `${row.product_code}::${col.field}`;
    const hasPendingChange = pendingChanges[cellKey];
    
    // 表示値を取得
    let displayValue;
    if (hasPendingChange) {
      displayValue = hasPendingChange.newValue !== '' ? hasPendingChange.newValue : '-';
    } else {
      displayValue = col.render ? col.render(row, 0) : (row[col.field] || '-');
    }
    
    // セルの実際の表示幅と内容の幅を比較
    const cell = e.currentTarget;
    const cellWidth = cell.offsetWidth;
    const contentWidth = cell.scrollWidth;
    
    // 内容がはみ出している場合のみツールチップを表示
    if (contentWidth > cellWidth && displayValue && displayValue !== '-') {
      const rect = cell.getBoundingClientRect();
      setTooltip({
        show: true,
        text: String(displayValue),
        x: rect.left + rect.width / 2,
        y: rect.top - 8
      });
    }
  }, [pendingChanges, columns]);

  const handleCellMouseLeave = useCallback(() => {
    setTooltip({ show: false, text: '', x: 0, y: 0 });
  }, []);

  return (
    <div className="h-full flex flex-col relative">
      {tooltip.show && (
        <div
          className="fixed z-50 px-3 py-2 bg-slate-900 text-white text-sm rounded shadow-lg pointer-events-none"
          style={{
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            transform: 'translate(-50%, -100%)',
            maxWidth: '400px',
            wordBreak: 'break-word'
          }}
        >
          {tooltip.text}
          <div
            className="absolute top-full left-1/2 transform -translate-x-1/2"
            style={{
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '6px solid rgb(15 23 42)'
            }}
          />
        </div>
      )}
      {Object.keys(pendingChanges).length > 0 && (
        <div className="p-3 bg-yellow-50 border-b border-yellow-200 flex items-center justify-between">
          <span className="text-sm text-yellow-800">
            {Object.keys(pendingChanges).length}件の未保存の変更があります
          </span>
          <button
            onClick={handleSaveAll}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
          >
            すべて保存
          </button>
        </div>
      )}
      <div className="flex-1 overflow-auto">
        <table className="divide-y divide-slate-200 table-fixed w-full">
          <colgroup>
            {columns.map((col, idx) => (
              <col key={idx} style={{ width: col.width ? `${col.width}px` : 'auto' }} />
            ))}
          </colgroup>
          <thead className="bg-slate-50 sticky top-0 z-10">
            <tr>
              {columns.map((col, idx) => (
                <th
                  key={idx}
                  className={`px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50 ${
                    col.sticky ? 'sticky z-20' : ''
                  }`}
                  style={col.sticky ? { left: `${col.stickyLeft || 0}px` } : {}}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-100">
            {data.length === 0 && !loading ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-slate-500">
                  データがありません
                </td>
              </tr>
            ) : data.length > 0 ? (
              data.map((row, rowIdx) => {
                const isExpanded = expandedRows.has(row.product_code);
                const rowHistories = historyData[row.product_code] || [];
                // バッチIDでグループ化（展開時のみ計算）
                let groupedByBatch = {};
                if (isExpanded && rowHistories.length > 0) {
                  rowHistories.forEach(h => {
                    if (h.change_batch_id) {
                      if (!groupedByBatch[h.change_batch_id]) {
                        groupedByBatch[h.change_batch_id] = [];
                      }
                      groupedByBatch[h.change_batch_id].push(h);
                    }
                  });
                }
                
                const isSelected = selectedProductCode === row.product_code;
                const highlightClass = getRowClassName ? getRowClassName(row) : '';
                
                return (
                  <React.Fragment key={row.product_code}>
                    <tr
                      data-row-id={row.product_code}
                      onClick={() => onRowClick && onRowClick(row)}
                      className={`transition-colors ${
                        isSelected
                          ? 'bg-slate-100'
                          : highlightClass || 'hover:bg-slate-100'
                      }`}
                    >
                      {columns.map((col, colIdx) => {
                        const cellKey = `${row.product_code}::${col.field}`;
                        const hasPendingChange = pendingChanges[cellKey];
                        const isReadonly = col.readonly;
                        
                        const cellKeyForEditing = `${row.product_code}::${col.field}`;
                        const isEditing = editingCell === cellKeyForEditing;
                        
                        // 表示値を取得（ツールチップ用）
                        let tooltipText = '';
                        if (hasPendingChange) {
                          tooltipText = hasPendingChange.newValue !== '' ? hasPendingChange.newValue : '-';
                        } else {
                          tooltipText = col.render ? col.render(row, rowIdx) : (row[col.field] || '-');
                        }
                        
                        return (
                          <td 
                            key={colIdx}
                            className={`px-4 py-3 text-sm text-slate-900 cursor-pointer ${
                              col.sticky ? 'sticky z-10' : ''
                            } ${
                              hasPendingChange ? 'bg-yellow-50 border-l-2 border-yellow-400' : ''
                            } ${
                              !isEditing ? 'whitespace-nowrap overflow-hidden text-ellipsis' : ''
                            }`}
                            style={col.sticky ? { left: `${col.stickyLeft || 0}px` } : {}}
                            onClick={(e) => handleCellClick(row, col, e)}
                            onMouseEnter={(e) => handleCellMouseEnter(e, row, col)}
                            onMouseLeave={handleCellMouseLeave}
                            title={hasPendingChange ? '未保存の変更があります' : (isReadonly ? 'クリックで履歴を表示' : '')}
                          >
                            {renderCellContent(row, col, rowIdx)}
                          </td>
                        );
                      })}
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={columns.length} className="p-0 bg-slate-50">
                          <div className="border-t border-slate-200 max-w-full overflow-x-auto min-w-0">
                            <div className="bg-white rounded-lg border border-slate-200 p-4 w-full">
                              {renderExpandedToolbar && (() => {
                                const toolbar = renderExpandedToolbar(row);
                                if (!toolbar) return null;
                                return (
                                  <div className="mb-3" onClick={(e) => e.stopPropagation()}>
                                    {toolbar}
                                  </div>
                                );
                              })()}
                              <h3 className="font-semibold text-sm mb-3 text-slate-900 break-words">
                                変更履歴 - {row.product_disp || row.product_code} {row.product_name ? `(${row.product_name})` : ''} {row.size ? `[${row.size}]` : ''}
                              </h3>
                              {rowHistories.length === 0 ? (
                                <div className="text-left py-4 text-slate-500">履歴がありません</div>
                              ) : (
                                <div className="space-y-3">
                                  {Object.entries(groupedByBatch).map(([batchId, batchHistories]) => (
                                    <div key={batchId} className="border border-slate-200 rounded p-3">
                                      <div className="mb-2 text-xs text-slate-600 break-words">
                                        <strong>変更日時:</strong> {formatDateTime(batchHistories[0]?.changed_at)} |
                                        <strong> 実行者:</strong> {batchHistories[0]?.changed_by || '-'} | 
                                        <strong> 理由:</strong> {batchHistories[0]?.change_reason || '-'}
                                      </div>
                                      <div className="overflow-x-auto">
                                        <table className="text-xs" style={{ width: 'auto' }}>
                                          <colgroup>
                                            <col style={{ width: '120px' }} />
                                            <col style={{ width: '200px' }} />
                                            <col style={{ width: '200px' }} />
                                            <col style={{ width: '300px' }} />
                                          </colgroup>
                                          <thead className="bg-slate-50">
                                            <tr>
                                              <th className="px-3 py-2 text-left font-semibold text-slate-700 whitespace-nowrap">フィールド</th>
                                              <th className="px-3 py-2 text-left font-semibold text-slate-700 whitespace-nowrap">変更前</th>
                                              <th className="px-3 py-2 text-left font-semibold text-slate-700 whitespace-nowrap">変更後</th>
                                              <th className="px-3 py-2 text-left font-semibold text-slate-700 whitespace-nowrap">変更理由</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {batchHistories.map((h, idx) => (
                                              <tr key={idx} className="border-t border-slate-100">
                                                <td className="px-3 py-2 break-words text-slate-900">{h.field_label || h.field_name}</td>
                                                <td className="px-3 py-2 break-words text-slate-600">{h.old_value || '-'}</td>
                                                <td className="px-3 py-2 break-words text-slate-900 font-medium">{h.new_value || '-'}</td>
                                                <td className="px-3 py-2 break-words text-slate-700">{h.change_reason || '-'}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : null}
          </tbody>
        </table>
        {observerTarget && (
          <div ref={observerTarget} className="h-1 flex-shrink-0 bg-transparent" style={{ minHeight: '1px' }} />
        )}
      </div>
    </div>
  );
};
